import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useParams } from 'react-router-dom';
import { useMediaQuery } from 'react-responsive';
import { CardHeader, Typography } from '@material-ui/core';
import { useDropzone } from 'react-dropzone';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Grid from '@material-ui/core/Grid';
import CircularProgress from '@material-ui/core/CircularProgress';
import {
    createProductImagesService,
    deleteProductImagesService,
    deleteBulkProductImagesService,
} from '../../../services/products';
import { errorToast, successToast } from '../../../constants.js/misc';
import { ImageUpload, ImageIconUpload, DnDWrapper, ResponsiveStyles } from './ProductImages.styles';
import DeleteProductDialog from '../DeleteProductDialog/DeleteProductDialog';
import AddIconUploadImage from '../../../assets/icons/addiconimages.svg';
import UploadImage from '../../../assets/img/upload-image.svg';
import { resizeFile } from '../../../utils/file-resizer';
import { CurrentImage } from './components/CurrentImage';

const ProductImages = ({
    productId = null,
    storeId,
    onFilesLoaded,
    onFilesError,
    productData,
    handleImageDeletion,
    files,
    setFiles,
    elevation = 2,
    processId,
    setProductData,
    setImagesToReorder,
    imageLimit = 10,
}) => {
    const { t } = useTranslation();
    const { enqueueSnackbar } = useSnackbar();
    let { id } = useParams();
    const [hasImages, setHasImages] = useState(false);
    const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
    const [imageCompressionLoading, setImageCompressionLoading] = useState(false);
    const [currentId, setCurrentId] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [isMassive, setIsMassive] = useState(false);
    const [indexProductCover, setIndexProductCover] = useState(null);
    const isTabletOrMobile = useMediaQuery({ query: '(max-width: 600px)' });

    const imgExist =
        productData?.product_images !== undefined ? productData?.product_images?.length : 0;
    const maxImages = imageLimit - imgExist;

    const classes = ResponsiveStyles();

    useEffect(() => {
        if (productId) {
            if (files.length) {
                createProductImagesService(storeId, productId, files)
                    .then((res) => {
                        onFilesLoaded();
                    })
                    .catch((err) => {
                        onFilesError();
                        throw new Error(err);
                    });
            } else {
                onFilesLoaded();
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [productId]);

    /**
     * Deletes the currently selected image
     */
    const deleteImage = () => {
        setDeleteDialogOpen(false);
        if ((currentId && id) || processId) {
            const functionToCall = isMassive
                ? deleteBulkProductImagesService
                : deleteProductImagesService;
            setDeleting(true);
            functionToCall(storeId, id || processId, currentId)
                .then(() => {
                    handleImageDeletion(currentId);
                    setDeleting(false);
                    enqueueSnackbar(t('Product.deleteImageSuccess'), successToast);
                    setCurrentId(false);
                })
                .catch(() => {
                    setCurrentId(false);
                    setDeleting(false);
                    enqueueSnackbar(t('Product.deleteImageError'), errorToast);
                });
        }
    };

    const openDialogAndSetCurrentId = (image) => {
        setIsMassive(image.task_process_id ? true : false);
        setCurrentId(image.id);
        setDeleteDialogOpen(true);
    };

    const maxUncompressedSize = 0.6 * 1000 * 1000;
    const maxSize = 20 * 1000 * 1000;
    const { getRootProps, getInputProps, isDragActive } = useDropzone({
        accept: 'image/jpeg, image/png, image/webp',
        onDrop: async (files) => {
            let processedFiles = [];

            for (const file of files) {
                // Check if we've reached the maximum number of images
                if (processedFiles.length >= maxImages) {
                    enqueueSnackbar(t('Product.maxImagesReached'), errorToast);
                    break;
                }

                if (file.size > maxSize) {
                    enqueueSnackbar(`${file.name} ${t('Product.imageTooBig')}`, errorToast);
                    continue;
                }

                try {
                    if (file.size > maxUncompressedSize) {
                        setImageCompressionLoading(true);
                        const compressedFile = await resizeFile(file);
                        processedFiles.push(compressedFile);
                    } else {
                        processedFiles.push(file);
                    }
                } catch (e) {
                    enqueueSnackbar(`${file.name} ${t('Product.compressingError')}`, errorToast);
                    continue;
                }
            }

            setImageCompressionLoading(false);

            // Update files state with new processed files
            setFiles((prevFiles) => {
                const newFiles = [...prevFiles, ...processedFiles];
                return newFiles.slice(0, maxImages);
            });
        },
    });

    const removeImgPreview = (index) => {
        const items = files;
        items.splice(index, 1);
        setFiles([...items]);
    };

    const setNewImageAsProductCover = (index) => {
        if (index !== 0 && !productData?.product_images?.length) {
            const newFiles = [...files];
            [newFiles[0], newFiles[index]] = [newFiles[index], newFiles[0]];
            setFiles(newFiles);
            setIndexProductCover(0);
        }
    };

    const setExistingImageAsProductCover = (index) => {
        if (index !== 0) {
            const images = [...productData.product_images];
            [images[0], images[index]] = [images[index], images[0]];
            const imageIds = images.map((image) => image.id);
            setImagesToReorder(imageIds);
            setIndexProductCover(0);
            setProductData({ ...productData, product_images: images });
        }
    };

    useEffect(() => {
        if (productData.product_images && productData.product_images.length > 0) setHasImages(true);
        setIndexProductCover(0);
    }, [productData]);

    return (
        <Card elevation={elevation} style={{ marginBottom: '21px' }}>
            <CardHeader titleTypographyProps={{ variant: 'h6' }} title={t('Product.titleImages')} />
            <CardContent>
                <Grid container spacing={isTabletOrMobile ? 0 : 3}>
                    <Grid item className={isTabletOrMobile ? classes.mobile : classes.full}>
                        {isTabletOrMobile && (
                            <DnDWrapper
                                {...getRootProps()}
                                style={{
                                    display: 'inline-block',
                                    position: 'relative',
                                    top: '-34px',
                                    padding: 17,
                                }}
                            >
                                <input
                                    {...getInputProps()}
                                    disabled={files.length + imgExist >= imageLimit}
                                />
                                {imageCompressionLoading ? (
                                    <CircularProgress
                                        key={Math.random()}
                                        size={30}
                                        color="secondary"
                                    />
                                ) : null}
                                {isDragActive ? (
                                    <ImageUpload src={UploadImage}></ImageUpload>
                                ) : (
                                    <>
                                        {!hasImages &&
                                            productData &&
                                            files?.length === 0 &&
                                            productData?.product_images?.length === 0 && (
                                                <ImageUpload src={UploadImage}></ImageUpload>
                                            )}
                                        {!hasImages && files?.length === 0 && !productData && (
                                            <ImageUpload src={UploadImage}></ImageUpload>
                                        )}
                                        {maxImages > 0 && maxImages - files?.length !== 0 && (
                                            <ImageIconUpload
                                                asExitsFiles={
                                                    productData &&
                                                    productData?.product_images?.length > 0
                                                        ? true
                                                        : false
                                                }
                                                src={AddIconUploadImage}
                                            ></ImageIconUpload>
                                        )}
                                    </>
                                )}
                            </DnDWrapper>
                        )}

                        {hasImages &&
                            productData.product_images.map((image, index) => (
                                <CurrentImage
                                    isDeleting={deleting && currentId === image.id}
                                    handleClick={() => openDialogAndSetCurrentId(image)}
                                    key={index}
                                    image={process.env.REACT_APP_IMAGES_API_URL + image.picture_url}
                                    alt={productData.name}
                                    handleSetAsProductCover={() =>
                                        setExistingImageAsProductCover(index)
                                    }
                                    isProductCover={indexProductCover === index}
                                />
                            ))}
                        {files.map((image, fileIndex) => {
                            return (
                                <>
                                    <CurrentImage
                                        handleSetAsProductCover={() =>
                                            setNewImageAsProductCover(fileIndex)
                                        }
                                        handleClick={() => removeImgPreview(fileIndex)}
                                        key={fileIndex}
                                        image={URL.createObjectURL(image)}
                                        isProductCover={
                                            indexProductCover === fileIndex &&
                                            !productData?.product_images?.length
                                        }
                                    />
                                </>
                            );
                        })}
                        <>
                            {!isTabletOrMobile && (
                                <DnDWrapper {...getRootProps()}>
                                    <input
                                        {...getInputProps()}
                                        disabled={files.length + imgExist >= imageLimit}
                                    />
                                    {imageCompressionLoading ? (
                                        <CircularProgress
                                            key={Math.random()}
                                            size={30}
                                            style={{ margin: '10px' }}
                                            color="secondary"
                                        />
                                    ) : null}
                                    {isDragActive ? (
                                        <ImageUpload src={UploadImage}></ImageUpload>
                                    ) : (
                                        <>
                                            {!hasImages &&
                                                productData &&
                                                files?.length === 0 &&
                                                productData?.product_images?.length === 0 && (
                                                    <ImageUpload src={UploadImage}></ImageUpload>
                                                )}
                                            {!hasImages && files?.length === 0 && !productData && (
                                                <ImageUpload src={UploadImage}></ImageUpload>
                                            )}
                                            {maxImages > 0 && maxImages - files?.length !== 0 && (
                                                <ImageIconUpload
                                                    asExitsFiles={
                                                        productData &&
                                                        productData?.product_images?.length > 0
                                                            ? true
                                                            : false
                                                    }
                                                    src={AddIconUploadImage}
                                                ></ImageIconUpload>
                                            )}
                                        </>
                                    )}
                                </DnDWrapper>
                            )}
                        </>
                    </Grid>
                </Grid>

                {hasImages && (
                    <DeleteProductDialog
                        open={deleteDialogOpen}
                        onClose={() => setDeleteDialogOpen(false)}
                        onConfirm={() => deleteImage()}
                        title={t('Product.deleteImageTitle')}
                        message={t('Product.deleteImageMessage')}
                        cancelText={t('Product.deleteImageCancel')}
                        confirmText={t('Product.deleteImageConfirm')}
                    />
                )}
            </CardContent>
            <Typography variant="h6" style={{ margin: '0 0 8px 16px' }}>
                {t('Product.dimensions')}
            </Typography>
            <Typography
                variant="caption"
                color="textSecondary"
                component="p"
                style={{ margin: '0 16px 16px 16px', lineHeight: 1.45 }}
            >
                {t('Product.imagesAcceptedFormats')}
            </Typography>
        </Card>
    );
};

const mapStateToProps = ({ auth, products }) => ({
    storeId: auth.storeId,
    processId: products.processId,
});

export default connect(mapStateToProps)(ProductImages);
